import { useEffect, useRef, useState } from "react";

import useWorldStore from "../store/worldStore";
import { ZONES } from "../data/zones";
import { getTaskComposerPlaceholder } from "./taskComposerConfig";
import TaskComposerCard from "./TaskComposerCard";
import { queueTaskAndKickoff } from "../lib/taskQueueActions";

function clampComposerPosition(position) {
  if (typeof window === "undefined") {
    return { left: position?.x || 0, top: position?.y || 0 };
  }

  const width = 360;
  const height = 220;
  const margin = 16;
  const baseX = (position?.x || 0) + 16;
  const baseY = (position?.y || 0) - 28;

  return {
    left: Math.max(margin, Math.min(baseX, window.innerWidth - width - margin)),
    top: Math.max(margin, Math.min(baseY, window.innerHeight - height - margin)),
  };
}

export default function DroppedAgentTaskComposer() {
  const composer = useWorldStore((state) => state.activeDroppedTaskComposer);
  const agents = useWorldStore((state) => state.agents);
  const closeComposer = useWorldStore((state) => state.closeDroppedTaskComposer);
  const agent = composer?.agentId
    ? agents.find((entry) => entry.id === composer.agentId) || null
    : null;
  const zone = ZONES.find((entry) => entry.id === composer?.zoneId) || null;

  useEffect(() => {
    if (!composer) return undefined;

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        closeComposer({ restore: true });
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [closeComposer, composer]);

  if (!composer || !agent) return null;

  const composerKey = [
    composer.agentId,
    composer.zoneId,
    composer.position?.x || 0,
    composer.position?.y || 0,
  ].join(":");

  return (
    <DroppedAgentTaskComposerInner
      key={composerKey}
      composer={composer}
      agent={agent}
      zone={zone}
      onClose={closeComposer}
    />
  );
}

function DroppedAgentTaskComposerInner({ composer, agent, zone, onClose }) {
  const [instruction, setInstruction] = useState("");
  const cardRef = useRef(null);

  const placeholder = getTaskComposerPlaceholder(zone?.id, zone?.name);
  const label = instruction.trim().slice(0, 40);
  const position = clampComposerPosition(composer.position);

  const handleSubmit = () => {
    const trimmed = instruction.trim();
    if (!trimmed) return;
    queueTaskAndKickoff(agent, trimmed, label || trimmed);
    onClose({ restore: false });
  };

  useEffect(() => {
    const handlePointerDown = (event) => {
      if (cardRef.current?.contains(event.target)) return;
      onClose({ restore: true });
    };

    window.addEventListener("pointerdown", handlePointerDown);
    return () => window.removeEventListener("pointerdown", handlePointerDown);
  }, [onClose]);

  return (
    <div
      ref={cardRef}
      style={{
        position: "fixed",
        left: position.left,
        top: position.top,
        width: 360,
        zIndex: 120,
      }}
    >
      <TaskComposerCard
        compact
        title={zone ? `${zone.name} Task` : "Task"}
        subtitle={agent.name}
        instruction={instruction}
        onInstructionChange={setInstruction}
        placeholder={placeholder}
        onSubmit={handleSubmit}
        onCancel={() => onClose({ restore: true })}
        submitLabel="Add Task"
      />
    </div>
  );
}
